import { useState, useContext, useEffect, forwardRef } from "react";
import {
  Grid,
  Typography,
  List,
  ListItem,
  Dialog,
  AppBar,
  Toolbar,
  DialogContent,
  Slide,
  IconButton,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { EventContext } from "../contexts/EventContext";
import request from "../utilities/requests";
import formatDate from "../utilities/formatDate";
import { toast } from "../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ManualInvoice = ({ open, close, eventId, orgId }) => {
  const { event } = useContext(EventContext) || {};
  const inferredOrgId = event?.org?._id || event?.org || orgId || null;

  const initialItems = eventId
    ? [
        {
          description: "",
          eventType: "speeddating",
          quantity: 1,
          price: 0,
          fee: false,
        },
      ]
    : [
        {
          description: "",
          product: "smsMonthlyFee",
          quantity: 1,
          price: 0,
          fee: false,
        },
      ];
  const [items, setItems] = useState(initialItems);
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("open");
  const [notes, setNotes] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    // Reset when dialog opens/closes
    if (!open) return;
    setPayload(null);
  }, [open]);

  const updateItem = (idx, key, value) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it))
    );
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      eventId
        ? {
            description: "",
            eventType: "speeddating",
            quantity: 1,
            price: 0,
            fee: false,
          }
        : {
            description: "",
            product: "smsMonthlyFee",
            quantity: 1,
            price: 0,
            fee: false,
          },
    ]);
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const amountDue = items.reduce((sum, it) => {
    const qty = Number(it.quantity || 0);
    const price = Number(it.price || 0);
    const line = (it.fee ? 0 : 1) * (qty * price);
    return sum + (Number.isFinite(line) ? line : 0);
  }, 0);

  const buildPayload = async () => {
    const orgId = inferredOrgId || undefined;
    const normalizedItems = items.map((i) => ({
      description: (i.description || "").trim() || "Line Item",
      eventType: i.eventType || undefined,
      product: i.product || undefined,
      fee: Boolean(i.fee),
      quantity: Number(i.quantity || 0),
      price: Number(i.price || 0),
      currency: "usd",
    }));

    const body = {
      orgId,
      eventId,
      items: normalizedItems,
      dueDate: dueDate || undefined,
      status: status || "open",
      notes: notes || undefined,
    };

    const result = await request(
      "contracts/invoice/manualcreate",
      "post",
      body
    );
    if (result.success) {
      toast.success("Invoice created");
      close();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      TransitionComponent={Transition}
      fullScreen
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={close}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Create Invoice
          </Typography>
          <Button color="inherit" onClick={buildPayload}>
            Create
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Invoice Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Due Date"
                    type="date"
                    fullWidth
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Select
                    fullWidth
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value={"open"}>open</MenuItem>
                    <MenuItem value={"draft"}>draft</MenuItem>
                    <MenuItem value={"pending"}>pending</MenuItem>
                    <MenuItem value={"paid"}>paid</MenuItem>
                    <MenuItem value={"disputed"}>disputed</MenuItem>
                    <MenuItem value={"void"}>void</MenuItem>
                    <MenuItem value={"uncollectible"}>uncollectible</MenuItem>
                    <MenuItem value={"deleted"}>deleted</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Line Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>{eventId ? "Event Type" : "Product"}</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Fee?</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell width="40%">
                        <TextField
                          fullWidth
                          placeholder="e.g., Flat monthly fee"
                          value={it.description}
                          onChange={(e) =>
                            updateItem(idx, "description", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell width="20%">
                        {eventId ? (
                          <Select
                            fullWidth
                            value={it.eventType}
                            onChange={(e) =>
                              updateItem(idx, "eventType", e.target.value)
                            }
                          >
                            <MenuItem value="speeddating">speeddating</MenuItem>
                            <MenuItem value="friend">friend</MenuItem>
                            <MenuItem value="mixer">mixer</MenuItem>
                            <MenuItem value="group">group</MenuItem>
                          </Select>
                        ) : (
                          <Select
                            fullWidth
                            value={it.product}
                            onChange={(e) =>
                              updateItem(idx, "product", e.target.value)
                            }
                          >
                            <MenuItem value="smsMonthlyFee">SMS Monthly Fee</MenuItem>
                            <MenuItem value="smsMessageFee">SMS Message Fee</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell width="10%" align="right">
                        <TextField
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          value={it.quantity}
                          onChange={(e) =>
                            updateItem(idx, "quantity", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell width="15%" align="right">
                        <TextField
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          value={it.price}
                          onChange={(e) =>
                            updateItem(idx, "price", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell width="10%">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!it.fee}
                              onChange={(e) =>
                                updateItem(idx, "fee", e.target.checked)
                              }
                            />
                          }
                          label=""
                        />
                      </TableCell>
                      <TableCell align="right" width="5%">
                        <Button
                          size="small"
                          onClick={() => removeItem(idx)}
                          disabled={items.length === 1}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" onClick={addItem}>
                  Add Line Item
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, position: "sticky", top: 8 }}>
              <Typography variant="subtitle1" gutterBottom>
                Summary
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2">Org ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {inferredOrgId || "(none)"}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2">Event ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {eventId}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body1">Amount Due</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  ${amountDue.toFixed(2)}
                </Typography>
              </Box>
              {payload && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="caption"
                    sx={{ display: "block", opacity: 0.7 }}
                  >
                    Payload built. Check console for JSON.
                  </Typography>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ManualInvoice;
